import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Loader2, TrendingUp, Building2, AlertTriangle, Rocket, AlertCircle } from "lucide-react";

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

export default function FleetStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/fleet-metrics");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="fleet-strip-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading fleet metrics…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const tiles = [
    { key: "mrr", testid: "fleet-mrr", label: "Monthly Recurring Revenue", value: fmtGBP(data.mrrGBP), sub: "across fleet", Icon: TrendingUp, cls: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white text-emerald-900", subCls: "text-emerald-700", iconBg: "bg-emerald-100", iconCls: "text-emerald-700" },
    { key: "orgs", testid: "fleet-orgs", label: "Active Organisations", value: data.activeOrgs, sub: "active tenants", Icon: Building2, cls: "border-slate-200 bg-white text-slate-900", subCls: "text-slate-500", iconBg: "bg-slate-100", iconCls: "text-slate-700" },
    { key: "risk", testid: "fleet-high-risk", label: "High-Risk Accounts", value: data.highRiskCount, sub: data.highRiskCount === 1 ? "needs attention" : "need attention", Icon: AlertTriangle, cls: "border-rose-200 bg-gradient-to-br from-rose-50 to-white text-rose-900", subCls: "text-rose-700", iconBg: "bg-rose-100", iconCls: "text-rose-700" },
    { key: "pipe", testid: "fleet-expansion", label: "Expansion Pipeline", value: fmtGBP(data.expansionPipelineGBP), sub: "per month upside", Icon: Rocket, cls: "border-indigo-200 bg-gradient-to-br from-indigo-50 to-white text-indigo-900", subCls: "text-indigo-700", iconBg: "bg-indigo-100", iconCls: "text-indigo-700" },
  ];

  return (
    <div className="space-y-3" data-testid="fleet-strip">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map(({ key, testid, label, value, sub, Icon, cls, subCls, iconBg, iconCls }) => (
          <Card key={key} className={`p-4 border ${cls}`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${iconBg}`}>
                <Icon className={`h-4 w-4 ${iconCls}`}/>
              </div>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${subCls}`}>{label}</div>
            </div>
            <div className="font-heading font-black text-3xl leading-tight">{value}</div>
            <div className={`text-[11px] mt-1 ${subCls}`}>{sub}</div>
          </Card>
        ))}
      </div>
      {data.concentrationWarning && (
        <Card className="p-3 border border-amber-300 bg-amber-50" data-testid="fleet-concentration-warning">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0"/>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">Revenue Concentration · {data.topConcentrationPct}%</div>
              <div className="text-xs text-amber-900 mt-0.5">Top 3 orgs drive {data.topConcentrationPct}% of MRR — diversify to reduce commercial risk.</div>
              {data.concentrationTopOrgs?.length > 0 && (
                <div className="text-[11px] text-amber-700 mt-0.5">{data.concentrationTopOrgs.map(o => `${o.name} (${fmtGBP(o.mrr)})`).join(" · ")}</div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
